import cx from 'classnames';
import Image from 'next/image';

import {Button, Card, Carousel, Text} from '#component/base';
import {CarouselProvider} from '#component/provider';
import {useCarousel, useViewportSize} from '#hook';
import {gridMarginVar, sprinkles} from '#style/sprinkles';

import {cards} from './HowItWorks.util';

import * as css from './HowItWorks.css';

/**
 * This component renders static content for the "Understanding Your Age" page.
 */
const InnerHowItWorks = ({className, ...otherProps}: React.ComponentPropsWithoutRef<'div'>) => {
  const {isMobile} = useViewportSize();
  const {goToNextSlide, goToPreviousSlide, ref} = useCarousel({
    gap: 24,
    slideWidth: isMobile ? 200 : 300,
  });

  return (
    <div className={cx(sprinkles({layout: 'grid'}), className)} {...otherProps}>
      <div
        className={sprinkles({
          colSpan: [4, 6, 10],
          colStart: [1, 2],
          dir: 'col',
          flex: 'flex',
          gap: [24, 48, 64],
        })}>
        <div className={sprinkles({flex: 'flex', content: 'between'})}>
          <div className={sprinkles({flex: 'flex', dir: 'col', gap: 24})}>
            <Text as="h2" color="deepBurgundy" variant="heading2">
              How it works
            </Text>

            <Text as="p" color="mineShaft">
              Explore how your sample transforms from a cheek swab to a TallyAge™.
            </Text>
          </div>

          <div className={sprinkles({flex: ['none', 'flex'], gap: 16})}>
            <Button
              aria-label="Go to previous slide"
              icon="ArrowLeft"
              onClick={goToPreviousSlide}
              variant="secondary"
            />
            <Button
              aria-label="Go to next slide"
              icon="ArrowRight"
              onClick={goToNextSlide}
              variant="secondary"
            />
          </div>
        </div>

        <Carousel
          className={css.carousel}
          ref={ref}
          paddings={[
            gridMarginVar,
            // Use CSS Calc values to derive the dynamic offset of grid column
            // widths and gutters on arbitrary viewport sizes.
            `calc(${gridMarginVar} + ((100vw - ${gridMarginVar} * 2) / 8)`,
            `calc(${gridMarginVar} + ((100vw - ${gridMarginVar} * 2) / 12)`,
          ]}
          widths={['100vw', '100vw', '100vw']}>
          {cards.map(({copy, image}, i) => (
            <Card key={i} className={css.card} variant="white">
              <div className={sprinkles({flex: 'flex', dir: 'col'})}>
                <Text color="grapefruit" variant="heading2">
                  {i + 1}
                </Text>

                {image && <Image alt="" aria-hidden src={image} width={720} height={480} />}
              </div>

              <Text color="mineShaft" variant="caption">
                {copy}
              </Text>
            </Card>
          ))}
        </Carousel>
      </div>
    </div>
  );
};

export const HowItWorks = () => (
  <CarouselProvider>
    <InnerHowItWorks />
  </CarouselProvider>
);
